/**
 * Authentication Store
 *
 * Manages authentication state using Zustand with persistence to localStorage.
 * Handles login (email/password, Google, Apple), logout, registration, and
 * session initialization. Supports workspace switching between personal and
 * school contexts, and provides role-based selectors for authorization checks.
 *
 * Persists only tokens and active workspace; user data is rehydrated from
 * the API on app load via {@link AuthActions.initialize}.
 *
 * @module useAuthStore
 *
 * @example
 * ```tsx
 * import { useAuthStore, selectUserRole, selectHasAnyRole } from '@/store/useAuthStore'
 *
 * function ProfileHeader() {
 *   const { user, isAuthenticated, logout } = useAuthStore()
 *   const role = useAuthStore(selectUserRole)
 *   const isAdmin = useAuthStore(selectHasAnyRole(['platform_admin', 'school_admin']))
 *
 *   if (!isAuthenticated) return <LoginPrompt />
 *
 *   return (
 *     <header>
 *       <span>{user?.displayName} ({role})</span>
 *       {isAdmin && <AdminBadge />}
 *       <button onClick={logout}>Log out</button>
 *     </header>
 *   )
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User, AuthTokens, UserRole, LoginData, RegisterData } from '../types'
import type { ContextType } from '../types/context.types'
import { authApi, apiConfig } from '../api'
import { realAuthApi } from '../api/auth.real.api'
import { setAuthCookie, clearAuthCookie } from '../lib/authCookie'
import {
  buildContextsFromLegacyRole,
  getDefaultActiveContext,
  hasContext,
  getRoleInContext,
  getAvailableContexts,
  isSchoolAdmin as isSchoolAdminHelper,
  isHeadSchoolAdmin as isHeadSchoolAdminHelper,
  isPlatformAdmin as isPlatformAdminHelper,
  isPlatformStaff,
  isSchoolStaff,
  isVendor as isVendorHelper,
  hasLinkedChildren,
  isTier1 as isTier1Helper,
  legacyRoleMatchesContexts,
} from '../lib/contextHelpers'

/** Enrich a user with contexts if they don't have them yet. */
function enrichUserWithContexts(user: User): User {
  if (user.contexts) return user
  return { ...user, contexts: buildContextsFromLegacyRole(user) }
}

/** Derive activeWorkspace from activeContext for legacy compat. */
function contextToWorkspace(context: ContextType): WorkspaceContext {
  return context === 'school' ? 'school' : 'personal'
}

/**
 * Active workspace context
 */
export type WorkspaceContext = 'personal' | 'school'

/**
 * Auth store state shape.
 *
 * Contains the authenticated user, JWT tokens, loading/error flags,
 * and the active workspace context (personal vs. school).
 */
interface AuthState {
  // User state
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean
  error: string | null

  // Context (new model)
  activeContext: ContextType

  // Workspace context (legacy — kept for backward compat)
  activeWorkspace: WorkspaceContext
}

/**
 * Auth store action methods.
 *
 * Includes auth operations (login, register, logout), session management
 * (initialize, refresh), workspace switching, and dev-mode role switching.
 */
interface AuthActions {
  // Auth operations
  login: (data: LoginData) => Promise<boolean>
  googleLogin: (credential: string) => Promise<boolean>
  appleLogin: (data: { idToken: string; authorizationCode: string; user?: { email?: string; name?: { firstName?: string; lastName?: string } } }) => Promise<boolean>
  register: (data: RegisterData) => Promise<boolean>
  logout: () => Promise<void>

  // Session management
  initialize: () => Promise<void>
  refreshTokens: () => Promise<boolean>

  // Context switching (new)
  switchContext: (context: ContextType) => void

  // Workspace (legacy — delegates to switchContext)
  switchWorkspace: (workspace: WorkspaceContext) => void

  // Dev tools - role switching
  switchRole: (role: UserRole, displayName?: string) => void

  // Profile updates
  updateUser: (data: Partial<User>) => void

  // State management
  setError: (error: string | null) => void
  clearError: () => void
}

type AuthStore = AuthState & AuthActions

/**
 * Initial state
 */
const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
  activeContext: 'personal' as ContextType,
  activeWorkspace: 'personal',
}

/**
 * Auth store with persistence
 */
export const useAuthStore = createWithEqualityFn<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Login with email and password
       */
      login: async (data: LoginData): Promise<boolean> => {
        set({ isLoading: true, error: null })

        const response = await authApi.login(data)

        if (response.success) {
          const user = enrichUserWithContexts(response.data.user)
          const activeContext = getDefaultActiveContext(user)
          set({
            user,
            tokens: response.data.tokens,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            activeContext,
            activeWorkspace: contextToWorkspace(activeContext),
          })
          setAuthCookie(response.data.tokens, user.displayName)
          return true
        } else {
          set({
            isLoading: false,
            error: response.error ?? 'Login failed',
          })
          return false
        }
      },

      /**
       * Login with Google OAuth
       */
      googleLogin: async (credential: string): Promise<boolean> => {
        // Only works with real API
        if (apiConfig.useMockApi) {
          set({ error: 'Google login requires real API backend' })
          return false
        }

        set({ isLoading: true, error: null })

        const response = await realAuthApi.googleLogin({ credential })

        if (response.success) {
          const user = enrichUserWithContexts(response.data.user)
          const activeContext = getDefaultActiveContext(user)
          set({
            user,
            tokens: response.data.tokens,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            activeContext,
            activeWorkspace: contextToWorkspace(activeContext),
          })
          setAuthCookie(response.data.tokens, user.displayName)
          return true
        } else {
          set({
            isLoading: false,
            error: response.error ?? 'Google login failed',
          })
          return false
        }
      },

      /**
       * Login with Apple Sign-In
       */
      appleLogin: async (data): Promise<boolean> => {
        // Only works with real API
        if (apiConfig.useMockApi) {
          set({ error: 'Apple login requires real API backend' })
          return false
        }

        set({ isLoading: true, error: null })

        const response = await realAuthApi.appleLogin(data)

        if (response.success) {
          const user = enrichUserWithContexts(response.data.user)
          const activeContext = getDefaultActiveContext(user)
          set({
            user,
            tokens: response.data.tokens,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            activeContext,
            activeWorkspace: contextToWorkspace(activeContext),
          })
          setAuthCookie(response.data.tokens, user.displayName)
          return true
        } else {
          set({
            isLoading: false,
            error: response.error ?? 'Apple login failed',
          })
          return false
        }
      },

      /**
       * Register a new user
       */
      register: async (data: RegisterData): Promise<boolean> => {
        set({ isLoading: true, error: null })

        const response = await authApi.register(data)

        if (response.success) {
          const user = enrichUserWithContexts(response.data.user)
          set({
            user,
            tokens: response.data.tokens,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            activeContext: 'personal' as ContextType,
            activeWorkspace: 'personal',
          })
          setAuthCookie(response.data.tokens, user.displayName)
          return true
        } else {
          set({
            isLoading: false,
            error: response.error ?? 'Registration failed',
          })
          return false
        }
      },

      /**
       * Logout current user
       */
      logout: async (): Promise<void> => {
        set({ isLoading: true })
        await authApi.logout()
        clearAuthCookie()
        set({
          ...initialState,
          isInitialized: true,
        })
      },

      /**
       * Initialize auth state on app load
       */
      initialize: async (): Promise<void> => {
        // If already initialized, skip
        if (get().isInitialized) return

        set({ isLoading: true })

        const response = await authApi.getCurrentUser()

        if (response.success && response.data) {
          const user = enrichUserWithContexts(response.data)
          const activeContext = getDefaultActiveContext(user)
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            activeContext,
            activeWorkspace: contextToWorkspace(activeContext),
          })
          const tokens = get().tokens
          if (tokens) setAuthCookie(tokens, user.displayName)
        } else {
          clearAuthCookie()
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          })
        }
      },

      /**
       * Refresh access token
       */
      refreshTokens: async (): Promise<boolean> => {
        const { tokens } = get()
        if (!tokens?.refreshToken) return false

        const response = await authApi.refreshToken(tokens.refreshToken)

        if (response.success) {
          set({ tokens: response.data.tokens })
          const { user } = get()
          if (user) setAuthCookie(response.data.tokens, user.displayName)
          return true
        } else {
          // Refresh failed, logout user
          await get().logout()
          return false
        }
      },

      /**
       * Switch active context
       */
      switchContext: (context: ContextType): void => {
        const { user } = get()
        if (!user) return

        if (!hasContext(user, context)) {
          console.warn(`Cannot switch to ${context} context: user does not have it`)
          return
        }

        set({
          activeContext: context,
          activeWorkspace: contextToWorkspace(context),
        })
      },

      /**
       * Switch between personal and school workspace
       * @deprecated Use switchContext instead
       */
      switchWorkspace: (workspace: WorkspaceContext): void => {
        const context: ContextType = workspace === 'school' ? 'school' : 'personal'
        get().switchContext(context)
      },

      /**
       * Switch user role (dev tool for testing)
       */
      switchRole: (role: UserRole, displayName?: string): void => {
        const { user } = get()
        if (!user) return

        const schoolRoles: UserRole[] = ['head_school_admin', 'school_admin', 'teacher', 'student']
        const needsSchool = schoolRoles.includes(role)

        const updatedUser: User = {
          ...user,
          role,
          displayName: displayName ?? user.displayName,
          schoolId: needsSchool ? 'school-001' : undefined,
          contexts: undefined, // Clear so bridge rebuilds
        }

        const enriched = enrichUserWithContexts(updatedUser)
        const activeContext = getDefaultActiveContext(enriched)

        set({
          user: enriched,
          activeContext,
          activeWorkspace: contextToWorkspace(activeContext),
        })
      },

      /**
       * Update user profile data (merge partial fields)
       */
      updateUser: (data: Partial<User>): void => {
        const { user } = get()
        if (!user) return
        set({ user: { ...user, ...data } })
      },

      /**
       * Set error message
       */
      setError: (error: string | null): void => {
        set({ error })
      },

      /**
       * Clear error message
       */
      clearError: (): void => {
        set({ error: null })
      },
    }),
    {
      name: 'docurious-auth',
      storage: createJSONStorage(() => localStorage),
      // Only persist tokens (user is rehydrated from API)
      partialize: (state) => ({
        tokens: state.tokens,
        activeWorkspace: state.activeWorkspace,
        activeContext: state.activeContext,
      }),
    }
  ),
  shallow,
)

// ============================================================================
// Selectors (for derived state)
// ============================================================================

/**
 * Select the current user's role.
 *
 * @param state - Auth store state
 * @returns The user's {@link UserRole} or `null` if not authenticated
 *
 * @example
 * ```ts
 * const role = useAuthStore(selectUserRole) // 'student' | 'teacher' | null
 * ```
 */
export const selectUserRole = (state: AuthStore): UserRole | null => state.user?.role ?? null

/**
 * Check if the current user is under 13 (COPPA-relevant).
 *
 * @param state - Auth store state
 * @returns `true` if the user is flagged as under 13
 */
export const selectIsUnder13 = (state: AuthStore): boolean => state.user?.isUnder13 ?? false

/**
 * Check if the current user is a minor (under 18) based on date of birth.
 *
 * @param state - Auth store state
 * @returns `true` if the user is under 18
 */
export const selectIsMinor = (state: AuthStore): boolean => {
  if (!state.user?.dateOfBirth) return false
  const dob = new Date(state.user.dateOfBirth)
  const now = new Date()
  let age = now.getFullYear() - dob.getFullYear()
  const monthDiff = now.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age--
  }
  return age < 18
}

/**
 * Create a selector that checks if the user has a specific role.
 *
 * @param role - The role to check against
 * @returns Selector function returning `true` if the user's role matches
 *
 * @example
 * ```ts
 * const isTeacher = useAuthStore(selectHasRole('teacher'))
 * ```
 */
export const selectHasRole = (role: UserRole) => (state: AuthStore): boolean =>
  state.user ? legacyRoleMatchesContexts(state.user, [role]) : false

/**
 * Create a selector that checks if the user has any of the specified roles.
 *
 * @param roles - Array of roles to match against
 * @returns Selector function returning `true` if the user's role is in the list
 *
 * @example
 * ```ts
 * const canManageSchool = useAuthStore(selectHasAnyRole(['school_admin', 'head_school_admin']))
 * ```
 */
export const selectHasAnyRole = (roles: UserRole[]) => (state: AuthStore): boolean =>
  state.user ? legacyRoleMatchesContexts(state.user, roles) : false

/**
 * Check if the user has an associated school (schoolId is set).
 *
 * @param state - Auth store state
 * @returns `true` if the user belongs to a school
 */
export const selectHasSchool = (state: AuthStore): boolean =>
  state.user ? hasContext(state.user, 'school') : false

/**
 * Check if the user has pending parental consent (COPPA two-tier flow).
 *
 * @param state - Auth store state
 * @returns `true` if parental consent status is `'pending'`
 */
export const selectNeedsConsent = (state: AuthStore): boolean =>
  state.user?.parentalConsentStatus === 'pending'

/**
 * Check if the user is currently operating in the school workspace context.
 *
 * @param state - Auth store state
 * @returns `true` if workspace is `'school'` and user has a school affiliation
 */
export const selectIsSchoolContext = (state: AuthStore): boolean =>
  state.activeContext === 'school' && (state.user ? hasContext(state.user, 'school') : false)

/**
 * Check if the user can access school features (has school affiliation + school role).
 *
 * @param state - Auth store state
 * @returns `true` if the user has a schoolId and a qualifying school role
 */
export const selectCanAccessSchool = (state: AuthStore): boolean =>
  state.user ? isSchoolStaff(state.user) : false

/**
 * Check if the user is a school admin at any level (head or regular).
 *
 * @param state - Auth store state
 * @returns `true` if the user's role is `'head_school_admin'` or `'school_admin'`
 */
export const selectIsSchoolAdmin = (state: AuthStore): boolean =>
  state.user ? isSchoolAdminHelper(state.user) : false

/**
 * Check if the user is the head school admin (highest school authority).
 *
 * @param state - Auth store state
 * @returns `true` if the user's role is `'head_school_admin'`
 */
export const selectIsHeadSchoolAdmin = (state: AuthStore): boolean =>
  state.user ? isHeadSchoolAdminHelper(state.user) : false

/**
 * Check if the user can verify track records (staff or platform admin).
 *
 * @param state - Auth store state
 * @returns `true` if the user's role is `'staff'` or `'platform_admin'`
 */
export const selectCanVerify = (state: AuthStore): boolean =>
  state.user ? isPlatformStaff(state.user) : false

/**
 * Check if the user is a platform admin (full platform oversight).
 *
 * @param state - Auth store state
 * @returns `true` if the user's role is `'platform_admin'`
 */
export const selectIsPlatformAdmin = (state: AuthStore): boolean =>
  state.user ? isPlatformAdminHelper(state.user) : false

/**
 * Check if the user has a Tier 1 (school-only) account with restricted features.
 *
 * Tier 1 accounts cannot create friend communities, access personal saved lists,
 * receive gifts, or use Dealer's Choice.
 *
 * @param state - Auth store state
 * @returns `true` if the user's account tier is `'tier_1_school_only'`
 */
export const selectIsTier1 = (state: AuthStore): boolean =>
  state.user ? isTier1Helper(state.user) : false

/**
 * Check if the user can create friend communities (requires Tier 2+ account).
 *
 * @param state - Auth store state
 * @returns `true` if the user is not restricted to Tier 1
 */
export const selectCanCreateCommunity = (state: AuthStore): boolean =>
  state.user ? !isTier1Helper(state.user) : false

/**
 * Check if the user can access their personal saved list (requires Tier 2+ account).
 *
 * @param state - Auth store state
 * @returns `true` if the user is not restricted to Tier 1
 */
export const selectCanAccessSavedList = (state: AuthStore): boolean =>
  state.user ? !isTier1Helper(state.user) : false

/**
 * Check if the user can receive gifted challenges (requires Tier 2+ account).
 *
 * @param state - Auth store state
 * @returns `true` if the user is not restricted to Tier 1
 */
export const selectCanReceiveGifts = (state: AuthStore): boolean =>
  state.user ? !isTier1Helper(state.user) : false

/**
 * Check if the user can use Dealer's Choice random challenge feature (requires Tier 2+ account).
 *
 * @param state - Auth store state
 * @returns `true` if the user is not restricted to Tier 1
 */
export const selectCanUseDealersChoice = (state: AuthStore): boolean =>
  state.user ? !isTier1Helper(state.user) : false

// ============================================================================
// New context-aware selectors
// ============================================================================

export const selectActiveContext = (state: AuthStore): ContextType => state.activeContext

export const selectAvailableContexts = (state: AuthStore): ContextType[] =>
  state.user ? getAvailableContexts(state.user) : ['personal']

export const selectHasContext = (context: ContextType) => (state: AuthStore): boolean =>
  state.user ? hasContext(state.user, context) : false

export const selectRoleInContext = (context: ContextType) => (state: AuthStore): string | null =>
  state.user ? getRoleInContext(state.user, context) : null

export const selectHasLinkedChildren = (state: AuthStore): boolean =>
  state.user ? hasLinkedChildren(state.user) : false

export const selectIsVendor = (state: AuthStore): boolean =>
  state.user ? isVendorHelper(state.user) : false
